/**
 * Carregamento do reCAPTCHA v3 por intenção do usuário.
 *
 * Antes o script era injetado no `load` de toda página que tivesse um
 * `.recaptcha_token` — e o formulário de WhatsApp do rodapé existe em todas.
 * Resultado: 340 KB e ~1,4 s de CPU no carregamento inicial, além dos cookies
 * de terceiro, sem nenhum usuário ter tocado num formulário.
 *
 * Agora o script só entra quando há intenção real (foco, toque ou clique em um
 * formulário com token) ou quando um envio pede o token. `regenerate_recaptcha`
 * aguarda o carregamento, então continua seguro chamá-la direto no submit.
 */
(() => {
    "use strict";

    const SCRIPT_SELECTOR = "script[src*='recaptcha/api.js']";
    let loadPromise = null;

    function recaptchaKey() {
        return typeof RECAPTCHA_PUBLIC_KEY === "string" ? RECAPTCHA_PUBLIC_KEY : "";
    }

    /** @returns {Promise<boolean>} true quando `grecaptcha` está pronto para uso. */
    function ensureRecaptcha() {
        if (loadPromise) {
            return loadPromise;
        }

        const key = recaptchaKey();
        if (!key) {
            loadPromise = Promise.resolve(false);
            return loadPromise;
        }

        loadPromise = new Promise((resolve) => {
            const ready = () => {
                if (typeof grecaptcha === "undefined" || typeof grecaptcha.ready !== "function") {
                    resolve(false);
                    return;
                }
                grecaptcha.ready(() => resolve(true));
            };

            const existing = document.querySelector(SCRIPT_SELECTOR);
            if (existing) {
                if (typeof grecaptcha !== "undefined") {
                    ready();
                } else {
                    existing.addEventListener("load", ready, { once: true });
                    existing.addEventListener("error", () => resolve(false), { once: true });
                }
                return;
            }

            const script = document.createElement("script");
            script.src = `https://www.google.com/recaptcha/api.js?render=${key}`;
            script.async = true;
            script.defer = true;
            script.addEventListener("load", ready, { once: true });
            script.addEventListener("error", () => resolve(false), { once: true });
            document.head.appendChild(script);
        });

        return loadPromise;
    }

    window.siteEnsureRecaptcha = ensureRecaptcha;

    // Pré-aquece na primeira intenção, para o token já estar pronto no submit.
    // Delegado no document: cobre formulários montados depois (modais, page-nav).
    const warmUp = (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }
        if (target.closest("button.btn-recaptcha")) {
            ensureRecaptcha();
            return;
        }

        const form = target.closest("form");
        if (form && form.querySelector(".recaptcha_token")) {
            ensureRecaptcha();
        }
    };

    ["focusin", "pointerdown", "touchstart"].forEach((type) => {
        document.addEventListener(type, warmUp, { passive: true, capture: true });
    });
})();

/**
 * Gera um novo token para o recaptcha do Google
 * @returns {Promise<string|null>}
 */
async function regenerate_recaptcha(form = null) {
    const loaded = await window.siteEnsureRecaptcha();
    if (!loaded) {
        return null;
    }

    if (form) {
        document.querySelectorAll("button.btn-recaptcha").forEach(btn => btn.disabled = true);
        const token = await grecaptcha.execute(RECAPTCHA_PUBLIC_KEY, { action: "contato" });
        document.querySelectorAll("input.recaptcha_token").forEach(input => input.value = token);
        document.querySelectorAll("button.btn-recaptcha").forEach(btn => btn.disabled = false);
        return token;
    }

    return await grecaptcha.execute(RECAPTCHA_PUBLIC_KEY, { action: "contato" });
}
